import { useOutletContext, useParams } from 'react-router-dom';
import Button from '@carrier-io/air-react/Button';
import Stack from '@carrier-io/air-react/Stack';
import { ConnectionContextType } from '../Connection';
import NextArrow from 'components/icons/NextArrow';
import ButtonBase from '@carrier-io/air-react/ButtonBase';
import Typography from '@carrier-io/air-react/Typography';
import { Align, TypographyColor, Variant } from 'constants/componentEnums';
import CopyToClipboard from './CopyToClipboard';
import DownloadIcon from 'components/icons/DownloadIcon';
import Divider from '@carrier-io/air-react/Divider';
import { config } from '../../../config';
import CertificateStatus from './CertificateStatus';
import { isProjectOngoing, isUploadingDisabled, ProjectOnboardingStatus } from 'store/models/Project';
import styles from '../../../styles/Connection.module.css';

const SetUpScript = () => {
  const { handleChange, cancelConfigure, vendorData } = useOutletContext<ConnectionContextType>();
  const { projectId = '' } = useParams();
  const formattedProjectId = projectId?.toLocaleLowerCase();
  const currentProject = vendorData?.projects?.find((project) => project.id === formattedProjectId);
  const projectStatus: ProjectOnboardingStatus = currentProject?.status ?? ProjectOnboardingStatus.DRAFT;
  const isCertificateAlreadyUploaded = isProjectOngoing(projectStatus);
  const disableUpload = isUploadingDisabled(projectStatus);

  return (
    <Stack direction="column" spacing={2} width="80%">
      <Typography
        color={TypographyColor.Text.Primary}
        sx={{
          marginTop: '20px',
          marginBottom: '20px',
        }}
        variant={Variant.H4}
        align={Align.Left}
      >
        Set Up Test Script
      </Typography>
      <Divider variant="full" />
      <Typography color={TypographyColor.Text.Primary} variant={Variant.H5} align={Align.Left}>
        Download Test Script
      </Typography>
      <Stack className={styles.downloadWrapper} direction="column" spacing={2} width="100%">
        <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
          Please press button below to start test application download. Once downloaded, navigate to the folder that
          contains loaded file and proceed with instructions below.
        </Typography>
        <a href="/app/test-app.zip" target="_blank">
          <Button
            data-testid="download-test-app"
            startIcon={<DownloadIcon />}
            color="primary"
            fullWidth={false}
            onClick={() => console.log('Download')}
            variant="outlined"
            sx={{
              width: 'fit-content',
            }}
          >
            Download test script
          </Button>
        </a>
      </Stack>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.H6} align={Align.Left}>
        Extract
      </Typography>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        Use <b>zip</b> or other suitable applicaiton to unpack test application from archive:
      </Typography>
      <CopyToClipboard text={'unzip test-app.zip -d test-app'} />
      <Typography color={TypographyColor.Text.Primary} variant={Variant.H6} align={Align.Left}>
        Install Dependencies
      </Typography>
      <CopyToClipboard text={'cd test-app'} />
      <CopyToClipboard text={'npm i'} />
      <Typography color={TypographyColor.Text.Primary} variant={Variant.H6} align={Align.Left}>
        Build application
      </Typography>
      <CopyToClipboard text={'npm run build'} />
      <Typography color={TypographyColor.Text.Primary} variant={Variant.H6} align={Align.Left}>
        Configure
      </Typography>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        The test app should have the following structure:
      </Typography>
      <div className={styles.testAppStructure}></div>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        Please copy device certificate file and device key file in <b>/ca</b> directory that is located in the root of
        test application.
      </Typography>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        Open <b>.env</b> file, which is located in the root of the test application and make the following updates:
      </Typography>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        <li>
          Replace <b>MQTT_URL</b> with your MQTT endpoint specified below:
        </li>
      </Typography>
      <CopyToClipboard text={config.mqttURL} />
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        <li>
          Replace <b>PROJECT_NAME</b> with your project name specified below:
        </li>
      </Typography>
      <CopyToClipboard text={`${vendorData?.vendorName}-${currentProject?.name}`} />
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        <li>
          Replace <b>CERT_PATH</b> with a proper path to child certificate that was generated.
        </li>
      </Typography>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        <li>
          Replace <b>KEY_PATH</b> with a proper path to child key that was generated.
        </li>
      </Typography>
      <Typography color={TypographyColor.Text.Primary} variant={Variant.Body1} align={Align.Left}>
        <li>
          Replace <b>DEVICE_NAME</b> with a common name of device entered during child key generation.
        </li>
      </Typography>
      {(isCertificateAlreadyUploaded || disableUpload) && <CertificateStatus status={projectStatus} />}
      <Divider variant="full" />
      <Stack direction="row" spacing={2} alignSelf="flex-end">
        <ButtonBase data-testid="step-cancel" onClick={cancelConfigure}>
          <Typography color="primary">Cancel</Typography>
        </ButtonBase>
        <Button
          data-testid="proceed-test-connection"
          endIcon={<NextArrow />}
          color="primary"
          fullWidth={false}
          onClick={() => handleChange(1, 2)}
        >
          Next: Test Your Connection
        </Button>
      </Stack>
    </Stack>
  );
};

export default SetUpScript;
